import React, { useState, useEffect } from "react";
import { Card, Table, Spin, Alert } from "antd";
import { FaMapMarkerAlt } from "react-icons/fa";
import axios from "axios";
import { base_url as baseURL } from "../utils/baseUrl";

const MostSellingLocations = ({ filterPeriod = "monthly", dateRange = null }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  useEffect(() => {
    fetchMostSellingLocations();
  }, [filterPeriod, dateRange]);

  const fetchMostSellingLocations = async () => {
    try {
      setLoading(true);
      let url = `${baseURL}/api/sales/most-selling-locations`;
      const params = new URLSearchParams();

      // Always send period parameter to ensure backend knows the filter
      params.append("period", filterPeriod);

      if (dateRange && dateRange.length === 2) {
        try {
          params.append("startDate", dateRange[0].format("YYYY-MM-DD"));
          params.append("endDate", dateRange[1].format("YYYY-MM-DD"));
        } catch (error) {
          console.error("Date formatting error:", error);
          // Fallback to manual date formatting
          const startDate = dateRange[0];
          const endDate = dateRange[1];
          params.append("startDate", `${startDate.$y}-${String(startDate.$M + 1).padStart(2, '0')}-${String(startDate.$D).padStart(2, '0')}`);
          params.append("endDate", `${endDate.$y}-${String(endDate.$M + 1).padStart(2, '0')}-${String(endDate.$D).padStart(2, '0')}`);
        }
      }

      // Add limit parameter - fixed at 10
      params.append("limit", 10);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url);
      const rawLocations = response.data.locations || [];

      // Process the locations - backend already groups by city/state
      const processedLocations = rawLocations.map(location => {
        const city = location.shipping_city || 'Unknown City';
        const state = location.shipping_state || 'Unknown State';
        const country = 'India'; // Default country

        return {
          city,
          state,
          country,
          orderCount: parseInt(location.total_orders) || 0,
          totalAmount: parseFloat(location.total_sales) || 0,
          uniqueCustomers: parseInt(location.unique_customers) || 0,
          averageOrderValue: parseFloat(location.average_order_value) || 0
        };
      });

      // Sort by order count (highest first) and take top 10
      const newLocations = processedLocations
        .sort((a, b) => b.orderCount - a.orderCount)
        .slice(0, 10);

      setLocations(newLocations);
      setError(null);
    } catch (err) {
      console.error("Error fetching most selling locations:", err);
      if (err.response?.status === 404) {
        setError("Most selling locations endpoint not found. Please ensure the backend server is running and the sales routes are deployed.");
      } else if (err.code === 'ERR_NETWORK') {
        setError("Unable to connect to the server. Please check your internet connection and try again.");
      } else {
        setError("Failed to fetch most selling locations. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };





  const columns = [
    {
      title: "Location",
      dataIndex: "city",
      key: "city",
      render: (city, record) => (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <FaMapMarkerAlt style={{ color: "#6B3F4F" }} />
            <span style={{ fontWeight: "500" }}>{city}</span>
          </div>
          <div style={{ fontSize: "12px", color: "#666", marginLeft: "24px" }}>
            {record.state}, {record.country}
          </div>
        </div>
      ),
    },
    {
      title: "Orders",
      dataIndex: "orderCount",
      key: "orderCount",
      align: "center",
      render: (count) => (
        <div style={{
          background: "#6B3F4F",
          color: "white",
          borderRadius: "12px",
          padding: "4px 12px",
          display: "inline-block",
          fontWeight: "500"
        }}>
          {count}
        </div>
      ),
    },
    {
      title: "Total Sales",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      render: (amount) => (
        <div style={{ fontWeight: "500", color: "#6B3F4F" }}>
          ₹{amount.toLocaleString()}
        </div>
      ),
    },
  ];



  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <FaMapMarkerAlt style={{ color: "#6B3F4F" }} />
          <span>Most Top Selling Locations - Top 10</span>
        </div>
      }

    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <button
              onClick={fetchMostSellingLocations}
              style={{
                background: "#6B3F4F",
                color: "white",
                border: "none",
                padding: "4px 8px",
                borderRadius: "4px",
                cursor: "pointer"
              }}
            >
              Retry
            </button>
          }
        />
      )}



      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading location data...</div>
        </div>
      ) : locations.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          <FaMapMarkerAlt style={{ fontSize: "48px", marginBottom: "16px" }} />
          <div>No location data available for the selected period</div>
        </div>
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={locations}
            rowKey={(record) => `${record.city}-${record.state}-${record.orderCount}-${record.totalAmount}`}
            pagination={false}
            size="middle"
            scroll={{ x: 800 }}
          />
        </>
      )}
    </Card>
  );
};

export default MostSellingLocations; 